/**
 * Module Structure de Base - Composant Principal
 * 
 * Interface utilisateur avec sliders selon cahier des charges
 * Modularité absolue - Isolation totale des erreurs
 */

import React, { useState, useEffect, useCallback } from 'react';
import {
  ParametresStructureBase,
  TypeForme,
  TypeMateriauGlobal,
  StructureBase,
  StructureBaseState
} from '../types';
import { callStructureBaseAPI, structureBaseAPI } from '../api/StructureBaseAPI';

// Styles CSS intégrés pour isolation - Thème sombre
const styles = {
  container: {
    padding: '15px',
    backgroundColor: '#2d2d2d',
    borderRadius: '8px',
    border: '1px solid #404040',
    margin: '5px 0'
  } as React.CSSProperties,
  header: {
    fontSize: '16px',
    fontWeight: 'bold',
    marginBottom: '5px',
    color: '#4dabf7',
    borderBottom: '2px solid #4dabf7',
    paddingBottom: '3px'
  } as React.CSSProperties,
  description: {
    margin: '0 0 10px 0',
    color: '#aaa',
    fontSize: '12px'
  } as React.CSSProperties,
  formGroup: {
    marginBottom: '8px'
  } as React.CSSProperties,
  label: {
    display: 'block',
    marginBottom: '5px',
    fontWeight: '500',
    color: '#e0e0e0',
    fontSize: '13px'
  } as React.CSSProperties,
  select: {
    width: '100%',
    padding: '8px',
    border: '1px solid #495057',
    borderRadius: '4px',
    fontSize: '14px',
    backgroundColor: '#333333',
    color: '#e0e0e0'
  } as React.CSSProperties,
  sliderContainer: {
    display: 'flex',
    alignItems: 'center',
    gap: '10px'
  } as React.CSSProperties,
  slider: {
    flex: 1,
    height: '6px',
    borderRadius: '3px',
    background: '#404040',
    outline: 'none',
    cursor: 'pointer'
  } as React.CSSProperties,
  value: {
    minWidth: '60px',
    textAlign: 'right' as const,
    fontWeight: '500',
    color: '#4dabf7'
  } as React.CSSProperties,
  error: {
    color: '#ff6b6b',
    fontSize: '12px',
    marginTop: '5px'
  } as React.CSSProperties,
  loading: {
    color: '#b0b0b0',
    fontStyle: 'italic'
  } as React.CSSProperties,
  info: {
    backgroundColor: '#1e3a5f',
    border: '1px solid #2c5aa0',
    borderRadius: '4px',
    padding: '10px',
    marginTop: '15px',
    fontSize: '12px',
    color: '#87ceeb'
  } as React.CSSProperties,
  statusZone: {
    minHeight: '25px',
    marginTop: '8px',
    display: 'flex',
    alignItems: 'center'
  } as React.CSSProperties,
  colorInput: {
    width: '60px',
    height: '40px',
    border: '1px solid #495057',
    borderRadius: '4px',
    cursor: 'pointer',
    backgroundColor: 'transparent'
  } as React.CSSProperties
};

interface StructureBaseModuleProps {
  onStructureChange?: (structure: StructureBase) => void;
  onError?: (error: string) => void;
}

/**
 * Composant principal du module Structure de Base
 * Tâches 1.1, 1.2, 1.3, 1.4 selon cahier des charges
 */
export const StructureBaseModule: React.FC<StructureBaseModuleProps> = ({
  onStructureChange,
  onError
}) => {
  // État du module (isolation complète)
  const [state, setState] = useState<StructureBaseState>({
    parametres: structureBaseAPI.getConfigurationParDefaut(),
    loading: false,
    initialized: false
  });

  const [nombreMontants, setNombreMontants] = useState(4);
  // Contraintes supprimées - utilisation de valeurs fixes

  /**
   * Génération de la structure avec isolation d'erreur
   */
  const genererStructure = useCallback(async (params: ParametresStructureBase, montants: number) => {
    try {
      setState(prev => ({ ...prev, loading: true, error: undefined }));

      // Validation supprimée - plus de contraintes

      // Appel API avec isolation d'erreur
      const structure = await callStructureBaseAPI(params, montants);
      
      setState(prev => ({
        ...prev,
        structure,
        loading: false,
        error: structure.error,
        initialized: true
      }));

      // Mettre à jour le slider avec le nombre réel de montants retourné par le serveur
      if (structure.nombreMontants && structure.nombreMontants !== montants) {
        setNombreMontants(structure.nombreMontants);
      }

      if (structure.error) {
        onError?.(structure.error);
      } else {
        onStructureChange?.(structure);
      }

    } catch (error) {
      const erreurMsg = `Erreur module structure-base: ${error instanceof Error ? error.message : 'Erreur inconnue'}`;
      setState(prev => ({ ...prev, loading: false, error: erreurMsg }));
      onError?.(erreurMsg);
    }
  }, [onStructureChange, onError]);



  /**
   * Mise à jour des paramètres avec réactivité temps réel
   */
  const updateParametres = useCallback((nouveauxParams: Partial<ParametresStructureBase>) => {
    const params = { ...state.parametres, ...nouveauxParams };
    
    // S'assurer que nombreCotes est défini pour les polygones
    if (params.type === 'polygone' && !params.nombreCotes) {
      params.nombreCotes = 6;
    }
    
    // Ajuster le nombre de montants selon le type de forme
    let nouveauNombreMontants = nombreMontants;
    if ((params.type === 'carre' || params.type === 'rectangle' || params.type === 'cercle' || params.type === 'ovale') && nombreMontants < 4) {
      nouveauNombreMontants = 4;
      setNombreMontants(4);
    } else if (params.type === 'polygone' && params.nombreCotes && nombreMontants < params.nombreCotes) {
      // Pour les polygones, s'assurer que le nombre de montants est au moins égal au nombre de côtés
      nouveauNombreMontants = params.nombreCotes;
      setNombreMontants(params.nombreCotes);
    }
    
    setState(prev => ({ ...prev, parametres: params }));
    
    // Génération temps réel
    genererStructure(params, nouveauNombreMontants);
  }, [state.parametres, nombreMontants, genererStructure]);

  /**
   * Mise à jour du nombre de montants
   */
  const updateNombreMontants = useCallback((nombre: number) => {
    setNombreMontants(nombre);
    genererStructure(state.parametres, nombre);
  }, [state.parametres, genererStructure]);

  // Génération initiale
  useEffect(() => {
    if (!state.initialized) {
      genererStructure(state.parametres, nombreMontants);
    }
  }, [state.initialized, genererStructure]);
  
  

  return (
    <div style={styles.container}>
      <div style={styles.header}>
        📐 Module 1: Structure de Base
      </div>
      <div style={styles.description}>
        Configuration de la forme et des dimensions de base
      </div>

      {/* Sélection du type de forme (Tâche 1.1) */}
      <div style={styles.formGroup}>
        <label style={styles.label}>Type de Forme:</label>
        <select
          style={styles.select}
          value={state.parametres.type}
          onChange={(e) => updateParametres({ type: e.target.value as TypeForme })}
        >
          <option value="rectangle">Rectangle</option>
          <option value="carre">Carré</option>
          <option value="cercle">Cercle</option>
          <option value="ovale">Ovale</option>
          <option value="polygone">Polygone</option>
        </select>
      </div>

      {/* Slider Largeur (Tâche 1.2) */}
      <div style={styles.formGroup}>
        <label style={styles.label}>Largeur:</label>
        <div style={styles.sliderContainer}>
          <input
            type="range"
            style={styles.slider}
            min="0.1"
            max="20"
            step="0.1"
            value={state.parametres.largeur}
            onChange={(e) => updateParametres({ largeur: parseFloat(e.target.value) })}
          />
          <span style={styles.value}>{state.parametres.largeur.toFixed(1)}m</span>
        </div>
      </div>

      {/* Slider Longueur (conditionnel) */}
      {(state.parametres.type === 'rectangle' || state.parametres.type === 'ovale') && (
        <div style={styles.formGroup}>
          <label style={styles.label}>Longueur:</label>
          <div style={styles.sliderContainer}>
            <input
              type="range"
              style={styles.slider}
              min="0.1"
            max="20"
              step="0.1"
              value={state.parametres.longueur || 3}
              onChange={(e) => updateParametres({ longueur: parseFloat(e.target.value) })}
            />
            <span style={styles.value}>{(state.parametres.longueur || 3).toFixed(1)}m</span>
          </div>
        </div>
      )}

      {/* Slider Hauteur */}
      <div style={styles.formGroup}>
        <label style={styles.label}>Hauteur:</label>
        <div style={styles.sliderContainer}>
          <input
            type="range"
            style={styles.slider}
            min="0.1"
            max="5"
            step="0.1"
            value={state.parametres.hauteur}
            onChange={(e) => updateParametres({ hauteur: parseFloat(e.target.value) })}
          />
          <span style={styles.value}>{state.parametres.hauteur.toFixed(1)}m</span>
        </div>
      </div>

      {/* Slider Nombre de côtés (pour polygone) */}
      {state.parametres.type === 'polygone' && (
        <div style={styles.formGroup}>
          <label style={styles.label}>Nombre de côtés:</label>
          <div style={styles.sliderContainer}>
            <input
              type="range"
              style={styles.slider}
              min="3"
            max="12"
              step="1"
              value={state.parametres.nombreCotes || 6}
              onChange={(e) => {
                const nouveauNombreCotes = parseInt(e.target.value);
                updateParametres({ nombreCotes: nouveauNombreCotes });
                // Ajuster le nombre de montants si nécessaire
                if (nombreMontants < nouveauNombreCotes) {
                  setNombreMontants(nouveauNombreCotes);
                }
              }}
            />
            <span style={styles.value}>{state.parametres.nombreCotes || 6}</span>
          </div>
        </div>
      )}

      {/* Sélection du matériau global */}
      <div style={styles.formGroup}>
        <label style={styles.label}>Matériau:</label>
        <select
          style={styles.select}
          value={state.parametres.materiau}
          onChange={(e) => updateParametres({ materiau: e.target.value as TypeMateriauGlobal })}
        >
          <option value={TypeMateriauGlobal.FER_A_BETON}>Fer à béton (rond)</option>
          <option value={TypeMateriauGlobal.TUBE_CARRE}>Tube carré</option>
        </select>
      </div>

      {/* Slider Épaisseur globale */}
      <div style={styles.formGroup}>
        <label style={styles.label}>Épaisseur:</label>
        <div style={styles.sliderContainer}>
          <input
            type="range"
            style={styles.slider}
            min="5"
            max={state.parametres.materiau === TypeMateriauGlobal.TUBE_CARRE ? "100" : "50"}
            step="1"
            value={state.parametres.epaisseur}
            onChange={(e) => updateParametres({ epaisseur: parseFloat(e.target.value) })}
          />
          <span style={styles.value}>{state.parametres.epaisseur.toFixed(0)}mm</span>
        </div>
      </div>

      {/* Sélection de couleur globale */}
      <div style={styles.formGroup}>
        <label style={styles.label}>Couleur de la tonnelle:</label>
        <input
          type="color"
          style={styles.colorInput}
          value={state.parametres.couleur}
          onChange={(e) => updateParametres({ couleur: e.target.value })}
        />
      </div>

      {/* Slider Nombre de montants (Tâche 1.4) */}
      <div style={styles.formGroup}>
        <label style={styles.label}>Nombre de montants:</label>
        <div style={styles.sliderContainer}>
          <input
            type="range"
            style={styles.slider}
            min={state.parametres.type === 'carre' || state.parametres.type === 'rectangle' || state.parametres.type === 'cercle' || state.parametres.type === 'ovale' ? "4" : state.parametres.type === 'polygone' ? (state.parametres.nombreCotes || 3).toString() : "1"}
            max="50"
            step="1"
            value={nombreMontants}
            onChange={(e) => updateNombreMontants(parseInt(e.target.value))}
          />
          <span style={styles.value}>{nombreMontants}</span>
        </div>

      </div>

      {/* Zone de statut fixe */}
      <div style={styles.statusZone}>
        {state.error ? (
          <div style={styles.error}>❌ {state.error}</div>
        ) : null}
      </div>
    </div>
  );
};

export default StructureBaseModule;